/*
 * <BeToolbar> -- a row of controls acting as one bar (layout/toolbar.scss).
 * Every item stays its own Tab stop: in BeOS the bar is a navigation GROUP
 * (B_NAVIGABLE_JUMP), not a roving-focus widget, which also leaves a text
 * field's arrow keys to its caret.
 */

import type { AriaRole, CSSProperties } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";
import { BeRow, type BeRowProps } from "./Stack.tsx";

/* `align`, `band` and `pad` are the bar's own decisions: as props they would tie
 * with its rules or be overridden by them, and a prop must always apply. */
export type BeToolbarProps = Omit<BeRowProps, "align" | "band" | "pad" | "role"> & {
	/** Names the bar for assistive tech. */
	"aria-label": string;
	/** Defaults to `toolbar`. A lone field and its submit button is a `search`
	 * landmark instead: `toolbar` is for three or more controls, and a <form>
	 * may not carry it. */
	role?: AriaRole;
	/** Draw each button's label under its art. Off, the label is still its name. */
	labels?: boolean;
	/** Size every item to the widest, as BeMail does. */
	equal?: boolean;
};

export const BeToolbar = beComponent<BeToolbarProps, HTMLElement>("BeToolbar", (props, be) => {
	const { labels = true, equal, scale, className, style, ...rest } = props;
	return <BeRow role="toolbar" {...rest} {...be("be-toolbar", !labels && "is-icons-only", equal && "is-equal")} />;
});

/** One button's state art, all at one size in source px. A missing state draws `enabled`. */
export type BeToolbarArt = {
	enabled: string;
	rollover?: string;
	pressed?: string;
	disabled?: string;
	width: number;
	height: number;
};

export type BeToolbarButtonProps = BeProps<
	"button",
	{
		art: BeToolbarArt;
		/** The label, and the accessible name even when the bar hides it. */
		children: string;
	}
>;

const artUrl = (src: string | undefined) => (src === undefined ? undefined : `url(${JSON.stringify(src)})`);

/** The art is the whole face: no frame is drawn, and every state is its own bitmap. */
export const BeToolbarButton = beComponent<BeToolbarButtonProps, HTMLButtonElement>("BeToolbarButton", (props, be) => {
	const { art, children, scale, className, style, type, ...rest } = props;
	const root = be("be-toolbar-button");
	const faces = {
		...root.style,
		"--be-toolbar-art-w": art.width,
		"--be-toolbar-art-h": art.height,
		"--be-toolbar-art-enabled": artUrl(art.enabled),
		"--be-toolbar-art-rollover": artUrl(art.rollover),
		"--be-toolbar-art-pressed": artUrl(art.pressed),
		"--be-toolbar-art-disabled": artUrl(art.disabled),
	} as CSSProperties;
	return (
		<button type={type ?? "button"} {...rest} {...root} style={faces}>
			<span className="be-toolbar-button__art" aria-hidden="true" />
			<span className="be-toolbar-button__label">{pixelText(children)}</span>
		</button>
	);
});
